<template>
    <div :class="[props.type === 'full' ? $style.buttons_full : $style.buttons_compact, props.row && $style.row]">
        <GoogleAuthNative
            v-if="isNativeLoginSupported"
            :size="buttonSize"
            :color="buttonColor"
            :onFinish="onGoogleNativeAuthFinish"
        />
        <GoogleAuth
            v-else
            :size="buttonSize"
            :onFinish="onGoogleAuthFinish"
            :class="props.row ? $style.button_row_override : ''"
        />
        <div :class="[$style.rest_providers, props.row ? $style.rest_providers_row : '']">
            <ProviderButton
                v-if="!appStore.isNativeAppEnv"
                id="microsoft"
                :color="buttonColor"
                :size="buttonSize"
                :disabled="!isMicrosoftScriptInitialized || isMicrosoftSigningIn"
                providerDisplayName="Microsoft"
                @click="handleMicrosoftSignIn"
            />
            <ProviderButton
                v-if="!appStore.isNativeAndroidEnv"
                id="apple"
                :color="buttonColor"
                :size="buttonSize"
                :disabled="!isAppleScriptInitialized || isAppleSigningIn"
                providerDisplayName="Apple"
                @click="handleAppleSignIn"
            />
        </div>
    </div>
</template>
<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, onMounted } from 'vue'

import GoogleAuthNative from '@/components/SocialLogins/GoogleAuthNative.vue'
import ProviderButton from '@/components/SocialLogins/ProviderButton.vue'
import { useAuthResult, type AuthMethod } from '@/composables/use-auth-result'
import { useAppStore } from '@/store/app-store'
import { useAppleAuthStore } from '@/store/apple-auth-store'
import { useMicrosoftAuthStore } from '@/store/microsoft-auth-store'

import GoogleAuth from './GoogleAuth.vue'

const props = defineProps<{
    loginSource: string | undefined
    type: 'full' | 'compact'
    onFinish?: (isSuccess: boolean) => void
    row?: boolean
}>()

const appStore = useAppStore()
const microsoftAuthStore = useMicrosoftAuthStore()
const appleAuthStore = useAppleAuthStore()
const isNativeLoginSupported = computed(
    () => appStore.isDebugMode || appStore.isVersionAtLeast({ ios: '1.0.6', android: '1.0.30' }),
)

const { initialized: isMicrosoftScriptInitialized, isSigningIn: isMicrosoftSigningIn } = storeToRefs(microsoftAuthStore)
const { initialized: isAppleScriptInitialized, isSigningIn: isAppleSigningIn } = storeToRefs(appleAuthStore)

const handleAuthResult = useAuthResult()

const buttonSize = computed(() => (props.type === 'full' ? 'l' : 's'))
const buttonColor = computed(() => (props.type === 'full' ? 'carbon-400' : 'carbon-200'))

function onAuthFinish(authMethod: AuthMethod, isSuccess: boolean, errorMessage?: string) {
    handleAuthResult({ authMethod, loginSource: props.loginSource, isSuccess, errorMessage })

    props.onFinish?.(isSuccess)
}

function onGoogleAuthFinish(isSuccess: boolean, errorMessage?: string) {
    onAuthFinish('google', isSuccess, errorMessage)
}

function onGoogleNativeAuthFinish(isSuccess: boolean, errorMessage?: string) {
    onAuthFinish('google', isSuccess, errorMessage)
}

function handleMicrosoftSignIn() {
    microsoftAuthStore.signIn((isSuccess, errorMessage) => {
        onAuthFinish('microsoft', isSuccess, errorMessage)
    })
}

function handleAppleSignIn() {
    appleAuthStore.signIn((isSuccess, errorMessage) => {
        onAuthFinish('apple', isSuccess, errorMessage)
    })
}

onMounted(() => {
    appleAuthStore.initGlobalSignIn()
    if (!appStore.isNativeIosEnv) {
        microsoftAuthStore.initGlobalSignIn()
    }
})
</script>
<style module>
.buttons_full,
.buttons_compact {
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.buttons_full {
    gap: 12px;
    width: 352px;
}

.buttons_compact {
    gap: 8px;
    width: 248px;
}

.rest_providers {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.buttons_compact .rest_providers {
    gap: 8px;
    flex-direction: row;
}

@media (--desktop-medium-plus) {
    .button_row_override {
        width: 352px;
    }

    .row {
        flex-direction: row;
        width: 100%;
        flex-wrap: wrap;
    }

    .rest_providers_row {
        flex-direction: row;
    }
}
</style>
